<script lang="ts" setup>
import { computed, ref, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import { PInput, PSelect } from "@profeat/ui-kit";

import { useTemplates } from "~/stores/template.store";
import { useTariffs } from "~/stores/tariffs.store";
import { useBroadcast } from "~/stores/broadcast.store";
import { useUser } from "~/stores/user.store";
import { dateFormatOptions } from "~/utils/format";
import { TemplateCategoryType } from "~/entities/template";

interface TemplateOption {
  value: string;
  text: string;
  category: TemplateCategoryType;
}

const { t } = useI18n();
const templatesStore = useTemplates();
const tariffsStore = useTariffs();
const broadcastStore = useBroadcast();
const userStore = useUser();

const showChatBotsSelect = ref(false);
const broadcastTitle = ref("");
const selectedTemplate = ref("");
const selectedChatBot = ref("");
const optionCategories = ref<{ [templateId: string]: TemplateCategoryType }>(
  {}
);

const templateHint = computed<string | undefined>(() => {
  if (selectedTemplate.value) {
    const category = optionCategories.value[selectedTemplate.value];

    const localeCategory = t(`profile.dialogs.${category.toLowerCase()}`);
    const cost = tariffsStore.templateCost[category];

    return userStore.user.isWzIntegration
      ? t("broadcast.templateCategory", { category: localeCategory })
      : t("broadcast.templateCost", { category: localeCategory, cost });
  }

  return undefined;
});
const templateOptions = computed<TemplateOption[]>(() =>
  templatesStore.channelTemplates.map(({ name, id, category }) => ({
    text: name,
    value: id,
    category,
  }))
);

const templateOptionsPlaceholder = computed(() => {
  return selectedTemplate.value !== "" ? t("broadcast.template") : "Не выбран";
});

const updateSelectedTemplateId = async (templateId: string) => {
  showChatBotsSelect.value = false;
  selectedChatBot.value = "";

  const template = templatesStore.channelTemplates.find(
    ({ id }) => id === templateId
  );

  broadcastStore.setSelectedTemplate(template || null);
  broadcastStore.clearSelectedFieldId();

  await nextTick();
  showChatBotsSelect.value = true;
};

onMounted(() => {
  optionCategories.value = templatesStore.channelTemplates.reduce(
    (categories, item) => ({ ...categories, [item.id]: item.category }),
    {}
  );

  const localeDate = new Date().toLocaleString(
    dayjs.locale(),
    dateFormatOptions
  );

  broadcastTitle.value = t("broadcast.defaultTitle", { date: localeDate });
  broadcastStore.setTitle(broadcastTitle.value);
  broadcastStore.selectedChatBotId = "";
});

const titleErrors = ref<string[]>([]);

function onTitleValueChanged(title: string) {
  broadcastStore.setTitle(title as string);
  validateTitle();
}

function validateTitle() {
  const errors = [];
  if (broadcastStore.titleAlreadyExists) {
    errors.push(t("broadcast.errors.titleAlreadyExists"));
  }
  if (broadcastTitle.value.length < 3) {
    errors.push(t("broadcast.errors.titleTooShort"));
  }
  titleErrors.value = errors;
}
</script>

<template>
  <div class="templates-form">
    <PInput
      v-model="broadcastTitle"
      @update:model-value="(title) => onTitleValueChanged(title as string)"
      :label="$t('broadcast.broadcast title')"
      size="large"
      :error="titleErrors.at(0)"
    />

    <PSelect
      v-if="templateOptions.length"
      v-model="selectedTemplate"
      class="template-select"
      @update:model-value="updateSelectedTemplateId"
      :options="templateOptions"
      :hint="templateHint"
      :placeholder="templateOptionsPlaceholder"
      :label="templateOptionsPlaceholder"
      :selected-on-top="true"
      max-height="290"
      size="large"
      :no-padding="true"
    >
      <template #default="slotProps">
        <slot :item="slotProps?.item">
          <div class="select-item">
            <div class="select-item-head">{{ slotProps?.item.text }}</div>
            <div class="select-item-content">
              {{
                $t(`profile.dialogs.${slotProps?.item.category.toLowerCase()}`)
              }}
            </div>
          </div>
        </slot>
      </template>
    </PSelect>
  </div>
</template>

<style lang="scss" scoped>
.templates-form {
  display: flex;
  flex-direction: column;
  gap: 32px;

  .select-item {
    gap: 4px;

    &-head {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      letter-spacing: 0;
      text-align: left;

      color: var(--common-black);
    }

    &-content {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0;
      text-align: left;

      color: var(--common-asphalt);
    }
  }

  .template-select {
    :deep(.dropdown) {
      padding-right: 5px;

      .dropdown-content {
        &::-webkit-scrollbar {
          width: 4px;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 25px;
          background: var(--common-rain, #d0dbe4);
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }
      }
    }
  }
}
</style>
